import React from 'react';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { Image } from '@plone/volto/components';
import useFormattedDate from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import { useTemaHref } from 'volto-ministerio-publico-federal/hooks/useNewsFilters';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';

const NewsItemCard = ({ item, variant = 'list' }) => {
  const href = flattenToAppURL(item['@id']);
  const { formatShortDate, formatShortTime } = useFormattedDate();
  const temaHref = useTemaHref();

  const formatDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
      iso: date.toISOString(),
      label: `${formatShortDate(date)} • ${formatShortTime(date)}`,
    };
  };
  // Tema editorial da notícia (behavior `noticia_meta`); itens legados do
  // tipo core "News Item" não têm o campo, então caem para o primeiro Subject.
  const category = item.tema || item.tema?.title;
  // A imagem pode vir do leadimage (`image`, tipo News) ou do
  // `preview_image_link` (Image filho, tipo News Item). O `image_field` da
  // busca indica a chave certa em `image_scales`; quando há `base_path`, o
  // download é relativo a ele (o Image filho), não ao href da notícia.
  const imageField = item.image_field || 'image';
  const scaleData = item.image_scales?.[imageField]?.[0];
  const previewDownload = scaleData?.scales?.preview?.download;
  const hasImage = !!previewDownload;
  const imageBase = scaleData?.base_path
    ? flattenToAppURL(scaleData.base_path)
    : href;
  const datetime = formatDateTime(item.effective);

  const isGridVariant = variant === 'grid';
  const containerClasses = isGridVariant
    ? 'flex flex-col gap-4 rounded-md border border-gray-200 bg-white p-4'
    : 'flex flex-col gap-5 rounded-md border border-gray-200 bg-white p-5 sm:flex-row';

  return (
    <article
      className={`news-item group ${containerClasses}`}
      data-origin={href}
      data-category={category}
      data-datetime={datetime?.iso}
    >
      {hasImage && (
        <div
          className={
            isGridVariant ? 'news-media w-full' : 'news-media shrink-0 sm:w-52'
          }
        >
          <Image
            item={item}
            src={flattenToAppURL(`${imageBase}/${previewDownload}`)}
            alt=""
            loading="lazy"
            className={
              isGridVariant
                ? 'h-40 w-full rounded object-cover'
                : 'h-40 w-full rounded object-cover sm:h-full'
            }
          />
        </div>
      )}

      <div className={isGridVariant ? 'min-w-0' : 'min-w-0 flex-1'}>
        {category && (
          <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--brand)]">
            <MPFLink
              href={temaHref(category)}
              className="rounded hover:underline focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/30"
            >
              {category}
            </MPFLink>
          </p>
        )}

        <h3 className="mt-2 text-base font-semibold text-on-surface-high">
          <MPFLink
            href={href}
            className="rounded hover:underline focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/30"
          >
            {item.title || item.id}
          </MPFLink>
        </h3>

        {item.description && (
          <p className="mt-2 text-sm text-on-surface line-clamp-3">
            {item.description}
          </p>
        )}

        {item.unit_title && (
          <div className="mt-3 text-xs text-gray-500">{item.unit_title}</div>
        )}

        {datetime && (
          <div className="mt-1 inline-flex items-center gap-2 text-xs text-gray-500">
            <time data-role="datetime" dateTime={datetime.iso}>
              {datetime.label}
            </time>
          </div>
        )}
      </div>
    </article>
  );
};

NewsItemCard.propTypes = {
  item: PropTypes.shape({
    '@id': PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    effective: PropTypes.string,
    image_scales: PropTypes.object,
    image_field: PropTypes.string,
    tema: PropTypes.shape({
      title: PropTypes.string,
      token: PropTypes.string,
    }),
    Subject: PropTypes.arrayOf(PropTypes.string),
    unit_title: PropTypes.string,
    id: PropTypes.string,
  }),
  variant: PropTypes.oneOf(['list', 'grid']),
};

export default NewsItemCard;
